import type { CompositePrimaryKey, Filter, GlobalSingleRelationFieldId, ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { RelatedItemKeyColumn, RelatedItemOperator } from './related-item-match';
import { FilterRelationalQuantifier, getCollectionPrimaryKeyFields, getM2MStructureSafe, getRelationConstraint, getRelationConstraintFields, isCrossSourceRelation } from '#layers/data-model/public';
import { nestFilterThroughRelations } from './filter-path';
import { buildTerminalMatch, isExclusionOperator } from './related-item-match';

/** How a related-item clause matches the items picked from its target collection. */
export const RelatedItemTargetKinds = {
	/**
	 * The terminal hop is a to-one relation within one data source whose collection holds the foreign
	 * key. The match is written on those foreign key columns and does not traverse the terminal hop.
	 */
	ForeignKey: 'foreign-key',
	/** The match traverses every hop and compares the target's primary key. */
	Traversal: 'traversal',
} as const;

type Kinds = typeof RelatedItemTargetKinds;

interface ForeignKeyColumn {
	/** The column on the collection holding the relation. */
	constrainedField: ResolvedMonospacePrimitiveField;
	/** The field it references on the target collection. */
	referencedField: ResolvedMonospacePrimitiveField;
}

export type RelatedItemTarget =
	| {
		readonly kind: Kinds['ForeignKey'];
		/** Every hop, M2M junction hops and the constrained relation included. */
		readonly relationFieldPath: GlobalSingleRelationFieldId[];
		/** The hops before the constrained relation; the match still traverses these. */
		readonly traversedRelations: ResolvedMonospaceSingleRelationField[];
		readonly relation: ResolvedMonospaceSingleRelationField;
		readonly foreignKey: ForeignKeyColumn[];
		readonly targetCollection: ResolvedMonospaceCollection;
	}
	| {
		readonly kind: Kinds['Traversal'];
		readonly relationFieldPath: GlobalSingleRelationFieldId[];
		readonly traversedRelations: ResolvedMonospaceSingleRelationField[];
		readonly primaryKeyFields: ResolvedMonospacePrimitiveField[];
		readonly targetCollection: ResolvedMonospaceCollection;
	};

interface RelatedItemTargetLookup {
	findSingleRelationFieldById: (id: GlobalSingleRelationFieldId) => ResolvedMonospaceSingleRelationField | undefined;
}

/**
 * Decides how a related-item clause over a relation path matches, so that parsing, serializing,
 * creating, and gating a clause agree. `relationFieldPath` is the real path, M2M junction hops included.
 */
export function resolveRelatedItemTarget(relationFieldPath: readonly GlobalSingleRelationFieldId[], schema: RelatedItemTargetLookup): RelatedItemTarget | null {
	const relations: ResolvedMonospaceSingleRelationField[] = [];

	for (const id of relationFieldPath) {
		const relation = schema.findSingleRelationFieldById(id);
		if (!relation) return null;
		relations.push(relation);
	}

	const relation = relations.at(-1);
	const targetCollection = relation?.oppositeCollection;
	if (!relation || !targetCollection) return null;

	const foreignKey = relation.isList || !staysWithinSource(relation, targetCollection) ? [] : foreignKeyColumns(relation);

	if (foreignKey.length > 0) {
		return {
			kind: RelatedItemTargetKinds.ForeignKey,
			relationFieldPath: [...relationFieldPath],
			traversedRelations: relations.slice(0, -1),
			relation,
			foreignKey,
			targetCollection,
		};
	}

	return traversalTarget(relationFieldPath, relations, targetCollection);
}

/** The same path matched by traversing every hop and comparing the target's primary key. */
export function toTraversalTarget(target: RelatedItemTarget): RelatedItemTarget | null {
	return traversalTarget(target.relationFieldPath, allRelations(target), target.targetCollection);
}

function traversalTarget(relationFieldPath: readonly GlobalSingleRelationFieldId[], relations: ResolvedMonospaceSingleRelationField[], targetCollection: ResolvedMonospaceCollection): RelatedItemTarget | null {
	const primaryKeyFields = getCollectionPrimaryKeyFields(targetCollection);
	if (primaryKeyFields.length === 0) return null;

	return { kind: RelatedItemTargetKinds.Traversal, relationFieldPath: [...relationFieldPath], traversedRelations: relations, primaryKeyFields, targetCollection };
}

/**
 * A cross-source key can outlive its target (ADR 053), where a match on the key would still hold but a
 * traversal would not.
 */
function staysWithinSource(relation: ResolvedMonospaceSingleRelationField, targetCollection: ResolvedMonospaceCollection): boolean {
	return !!relation.collection && !isCrossSourceRelation(relation.collection.sourceId, targetCollection.sourceId);
}

function foreignKeyColumns(relation: ResolvedMonospaceSingleRelationField): ForeignKeyColumn[] {
	const { constraint, thisSideConstrained } = getRelationConstraint(relation);
	if (!constraint || !thisSideConstrained) return [];

	const { constrainedFields, referencedFields } = getRelationConstraintFields(constraint);

	return constrainedFields.flatMap((constrainedField, index) => {
		const referencedField = referencedFields[index];
		return referencedField ? [{ constrainedField, referencedField }] : [];
	});
}

/**
 * Resolves a relation path as a picker selects it. An M2M field's opposite collection is the
 * junction, not the collection the user picks from, so the path continues through the junction's leg
 * to the target rather than matching opaque junction rows.
 */
export function resolveSelectedRelationTarget(fieldPath: readonly string[], schema: RelatedItemTargetLookup) {
	// A picker path that ends on a relation holds relation field ids.
	const selectedPath = fieldPath as readonly GlobalSingleRelationFieldId[];
	const selectedRelationId = selectedPath.at(-1);
	const selectedRelation = selectedRelationId ? schema.findSingleRelationFieldById(selectedRelationId) : undefined;
	if (!selectedRelationId || !selectedRelation) return null;

	const m2m = getM2MStructureSafe(selectedRelation);
	const target = resolveRelatedItemTarget(m2m ? [...selectedPath, m2m.junction.toTarget.id] : selectedPath, schema);
	if (!target) return null;

	return {
		target,
		selectedRelationId,
		// Picking from a to-many relation (an M2M always is one) is a membership match.
		isList: !!m2m || selectedRelation.isList,
	};
}

/** The fields of the target collection that a picked key carries. */
export function relatedItemKeyFields(target: RelatedItemTarget): ResolvedMonospacePrimitiveField[] {
	switch (target.kind) {
		case RelatedItemTargetKinds.ForeignKey:
			return target.foreignKey.map(({ referencedField }) => referencedField);
		case RelatedItemTargetKinds.Traversal:
			return target.primaryKeyFields;
		default: {
			const unhandled: never = target;
			return unhandled;
		}
	}
}

/** The columns the terminal match compares each picked key field on. */
export function relatedItemMatchColumns(target: RelatedItemTarget): RelatedItemKeyColumn[] {
	switch (target.kind) {
		case RelatedItemTargetKinds.ForeignKey:
			return target.foreignKey.map(({ constrainedField, referencedField }) => ({ key: referencedField.apiName, column: constrainedField.apiName, isNullable: constrainedField.isNullable }));
		case RelatedItemTargetKinds.Traversal:
			return traversedColumns(target.primaryKeyFields);
		default: {
			const unhandled: never = target;
			return unhandled;
		}
	}
}

/** Columns of the target collection a traversal compares each picked key field on. */
export function traversedColumns(fields: ResolvedMonospacePrimitiveField[]): RelatedItemKeyColumn[] {
	return fields.map((field) => ({ key: field.apiName, column: field.apiName, isNullable: false }));
}

/** Every hop of the path, the terminal hop included. */
function allRelations(target: RelatedItemTarget): ResolvedMonospaceSingleRelationField[] {
	switch (target.kind) {
		case RelatedItemTargetKinds.ForeignKey:
			return [...target.traversedRelations, target.relation];
		case RelatedItemTargetKinds.Traversal:
			return target.traversedRelations;
		default: {
			const unhandled: never = target;
			return unhandled;
		}
	}
}

/** Whether the match crosses a to-many hop, which then carries the polarity as `_some`/`_none`. */
export function traversesToMany(target: RelatedItemTarget): boolean {
	return target.traversedRelations.some((relation) => relation.isList);
}

/**
 * Serializes a match on the picked keys: `{ author_id: { _eq: 1 } }` on a foreign key, or
 * `{ posts: { _some: { id: { _in: [1] } } } }` through a to-many relation. A to-many hop carries the
 * polarity as its quantifier around a positive terminal; a path of to-one hops carries it inline.
 * Without keys the relation hop holds an empty match, which keeps the clause recognizable while
 * `toApplicableFilter` prunes it.
 */
export function serializeRelatedItemMatch(target: RelatedItemTarget, operator: RelatedItemOperator, keys: CompositePrimaryKey[]): Filter {
	const relations = allRelations(target);
	const toMany = traversesToMany(target);
	const quantifier = isExclusionOperator(operator) ? FilterRelationalQuantifier.None : FilterRelationalQuantifier.Some;
	const options = toMany ? { quantifier } : {};
	const lookup = (id: GlobalSingleRelationFieldId) => relations.find((relation) => relation.id === id);

	const condition = keys.length === 0
		? nestFilterThroughRelations(target.relationFieldPath, {}, lookup, options)
		: nestFilterThroughRelations(
				target.traversedRelations.map((relation) => relation.id),
				buildTerminalMatch(operator, keys, relatedItemMatchColumns(target), { positive: toMany }),
				lookup,
				options,
			);

	if (!condition) throw new Error(`[serializeRelatedItemMatch] relation not found in path: ${target.relationFieldPath.join(', ')}`);

	return condition;
}
